import type { Container, ReceiverWeeklyTarget } from '@/types/container';

/**
 * Calcola il numero della settimana (1-52) per una data specifica
 * Utilizza settimane semplici basate sull'anno solare
 */
export function getWeekNumber(date: Date): number {
    const startOfYear = new Date(date.getFullYear(), 0, 1);
    const dayOfYear = Math.floor((date.getTime() - startOfYear.getTime()) / (24 * 60 * 60 * 1000)) + 1;
    return Math.ceil(dayOfYear / 7);
}

/**
 * Ottiene la data di inizio e fine di una settimana specifica
 */
export function getWeekRange(year: number, weekNumber: number): { start: Date; end: Date } {
    const startOfYear = new Date(year, 0, 1);
    const startOfWeek = new Date(startOfYear.getTime() + (weekNumber - 1) * 7 * 24 * 60 * 60 * 1000);
    const endOfWeek = new Date(startOfWeek.getTime() + 6 * 24 * 60 * 60 * 1000);
    
    return { start: startOfWeek, end: endOfWeek };
}

/**
 * Ottiene la settimana corrente
 */
export function getCurrentWeek(): number {
    return getWeekNumber(new Date());
}

/**
 * Ottiene l'anno corrente
 */
export function getCurrentYear(): number {
    return new Date().getFullYear();
}

/**
 * Raggruppa i container per settimana basandosi sulla data ETA
 */
export function groupContainersByWeek(containers: Container[]): Map<number, Container[]> {
    const grouped = new Map<number, Container[]>();
    
    containers.forEach(container => {
        if (!container.eta) return;
        const weekNumber = getWeekNumber(container.eta);

        if (!grouped.has(weekNumber)) {
            grouped.set(weekNumber, []);
        }

        grouped.get(weekNumber)!.push(container);
    });
    
    return grouped;
}

/**
 * Interfaccia per i dati aggregati di una settimana
 */
export interface WeekData {
    weekNumber: number;
    year: number;
    dateRange: { start: Date; end: Date };
    containers: Container[];
    totalContainers: number;
    uniqueOrigins: string[];
    uniqueSenders: string[];
    totalValue: { amount: number; currency: string };
    commodities: string[];
    isCurrentWeek: boolean;
}

/**
 * Calcola i dati aggregati per una settimana specifica
 */
export function calculateWeekData(weekNumber: number, containers: Container[], year: number = getCurrentYear()): WeekData {
    const dateRange = getWeekRange(year, weekNumber);
    const currentWeek = getCurrentWeek();
    
    // Calcola origini uniche
    const uniqueOrigins = [...new Set(containers.map(c => c.route.from))];
    
    // Calcola mittenti unici
    const uniqueSenders = [...new Set(containers.map(c => c.shipperId))];
    
    // Calcola valore totale (convertendo tutto in USD per semplicità)
    const totalValue = containers.reduce((sum, container) => {
        // Per semplicità, assumiamo conversioni 1:1 per EUR e CAD
        return sum + container.value.amount;
    }, 0);
    
    // Calcola commodities uniche
    const commodities = [...new Set(containers.map(c => c.commodity))];
    
    return {
        weekNumber,
        year,
        dateRange,
        containers,
        totalContainers: containers.length,
        uniqueOrigins,
        uniqueSenders,
        totalValue: { amount: totalValue, currency: 'USD' },
        commodities,
        isCurrentWeek: weekNumber === currentWeek
    };
}

/**
 * Genera una lista di tutte le settimane dell'anno con i dati dei container
 */
export function generateYearlyWeekData(containers: Container[], year: number = getCurrentYear()): WeekData[] {
    const groupedContainers = groupContainersByWeek(containers);
    const weekDataList: WeekData[] = [];
    
    // Genera dati per tutte le 52 settimane
    for (let week = 1; week <= 52; week++) {
        const weekContainers = groupedContainers.get(week) || [];
        const weekData = calculateWeekData(week, weekContainers, year);
        weekDataList.push(weekData);
    }
    
    return weekDataList;
}

/**
 * Formatta una data in formato locale
 */
export function formatDate(date: Date, locale: string = 'it-IT'): string {
    return new Intl.DateTimeFormat(locale, {
        day: 'numeric',
        month: 'short'
    }).format(date);
}

/**
 * Formatta il range di date di una settimana
 */
export function formatWeekRange(weekData: WeekData, locale: string = 'it-IT'): string {
    const startFormatted = formatDate(weekData.dateRange.start, locale);
    const endFormatted = formatDate(weekData.dateRange.end, locale);
    return `${startFormatted} - ${endFormatted}`;
}

/**
 * Calcola la percentuale di raggiungimento di un target
 */
export function calculateTargetPercentage(actual: number, target: number): number {
    if (target === 0) return actual > 0 ? 100 : 0;
    return Math.min(100, Math.round((actual / target) * 100));
}

/**
 * Ottiene il numero del mese (1-12) da una data
 */
export function getMonthNumber(date: Date): number {
    return date.getMonth() + 1;
}

/**
 * Raggruppa i container per mese basandosi sulla data ETA
 */
export function groupContainersByMonth(containers: Container[]): Map<number, Container[]> {
    const grouped = new Map<number, Container[]>();
    
    containers.forEach(container => {
        if (!container.eta) return;
        const month = getMonthNumber(container.eta);

        if (!grouped.has(month)) {
            grouped.set(month, []);
        }

        grouped.get(month)!.push(container);
    });
    
    return grouped;
}

/**
 * Calcola il totale di container confermati e da confermare per una settimana
 */
export function getConfirmedVsPending(containers: Container[]): {
    confirmed: number;
    pending: number;
    confirmedValue: number;
    pendingValue: number;
} {
    let confirmed = 0;
    let pending = 0;
    let confirmedValue = 0;
    let pendingValue = 0;
    
    containers.forEach(container => {
        const isConfirmed = container.isConfirmed !== false; // default true per retrocompatibilità
        const value = container.value.amount; // Assumiamo conversione 1:1 per semplicità
        
        if (isConfirmed) {
            confirmed++;
            confirmedValue += value;
        } else {
            pending++;
            pendingValue += value;
        }
    });
    
    return { confirmed, pending, confirmedValue, pendingValue };
}

/**
 * Calcola aggregati mensili per container e revenue
 */
export function calculateMonthlyAggregates(
    containers: Container[]
): Array<{
    month: number;
    totalContainers: number;
    confirmedContainers: number;
    pendingContainers: number;
    totalRevenue: number;
    confirmedRevenue: number;
    pendingRevenue: number;
}> {
    const grouped = groupContainersByMonth(containers);
    const monthlyData: Array<{
        month: number;
        totalContainers: number;
        confirmedContainers: number;
        pendingContainers: number;
        totalRevenue: number;
        confirmedRevenue: number;
        pendingRevenue: number;
    }> = [];
    
    for (let month = 1; month <= 12; month++) {
        const monthContainers = grouped.get(month) || [];
        const { confirmed, pending, confirmedValue, pendingValue } = getConfirmedVsPending(monthContainers);
        
        monthlyData.push({
            month,
            totalContainers: monthContainers.length,
            confirmedContainers: confirmed,
            pendingContainers: pending,
            totalRevenue: confirmedValue + pendingValue,
            confirmedRevenue: confirmedValue,
            pendingRevenue: pendingValue,
        });
    }
    
    return monthlyData;
}

/**
 * Calcola aggregati annuali per container e revenue
 */
export function calculateYearlyAggregates(containers: Container[]): {
    totalContainers: number;
    confirmedContainers: number;
    pendingContainers: number;
    totalRevenue: number;
    confirmedRevenue: number;
    pendingRevenue: number;
} {
    const { confirmed, pending, confirmedValue, pendingValue } = getConfirmedVsPending(containers);
    
    return {
        totalContainers: containers.length,
        confirmedContainers: confirmed,
        pendingContainers: pending,
        totalRevenue: confirmedValue + pendingValue,
        confirmedRevenue: confirmedValue,
        pendingRevenue: pendingValue,
    };
}

/**
 * Calcola il target totale per un anno (target generico * 52 settimane)
 */
export function calculateYearlyTarget(defaultTarget: ReceiverWeeklyTarget | null): {
    containerTarget: number;
    revenueTarget: number;
} {
    if (!defaultTarget) {
        return { containerTarget: 0, revenueTarget: 0 };
    }
    
    return {
        containerTarget: defaultTarget.containerTarget * 52,
        revenueTarget: defaultTarget.revenueTarget * 52,
    };
}

/**
 * Calcola il target totale per un mese (target generico * numero settimane nel mese)
 */
export function calculateMonthlyTarget(
    defaultTarget: ReceiverWeeklyTarget | null,
    month: number,
    year: number
): {
    containerTarget: number;
    revenueTarget: number;
} {
    if (!defaultTarget) {
        return { containerTarget: 0, revenueTarget: 0 };
    }
    
    // Calcola il numero di settimane nel mese
    const startOfMonth = new Date(year, month - 1, 1);
    const endOfMonth = new Date(year, month, 0);
    const startWeek = getWeekNumber(startOfMonth);
    const endWeek = getWeekNumber(endOfMonth);
    const weeksInMonth = endWeek - startWeek + 1;
    
    return {
        containerTarget: defaultTarget.containerTarget * weeksInMonth,
        revenueTarget: defaultTarget.revenueTarget * weeksInMonth,
    };
}
